/**
 * A bar for a bounded 0–100 measurement, and the one component allowed to render a
 * rubric score.
 *
 * `Meter` bars **score**, not Elo. Elo starts at 1500 with K=24 and is unbounded, so
 * there is no maximum to normalise a bar against; a bar drawn against the highest
 * Elo on the current page would silently rescale itself as rows were added. Elo
 * stays a mono number.
 *
 * A `null` value draws a hollow track and the words "Not measured". It never draws a
 * zero-width fill, because an empty bar and a bar that was never filled look
 * identical and mean opposite things — "we measured this and it scored nothing"
 * versus "nobody measured this".
 */
import type { ReactNode } from 'react'
import { cn, formatNumber } from '../../lib/utils'

interface MeterProps {
  /** 0–100. `null`/`undefined` renders as not measured, never as 0. */
  value: number | null | undefined
  /** Accessible name — "rubric score", "evidence coverage". */
  label: string
  /** Width of the track. */
  className?: string
  /** Suffix beside the figure, e.g. the coverage that must accompany a score. */
  children?: ReactNode
}

export function Meter({ value, label, className, children }: MeterProps) {
  const measured = value !== null && value !== undefined && !Number.isNaN(value)
  const pct = measured ? Math.max(0, Math.min(100, value)) : 0
  return (
    <div className="flex items-center gap-2">
      <div
        role="meter"
        aria-label={label}
        aria-valuenow={measured ? pct : undefined}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuetext={measured ? undefined : 'Not measured'}
        className={cn(
          'h-1.5 w-20 shrink-0 overflow-hidden rounded-full',
          measured ? 'bg-slate-800' : 'border border-dashed border-slate-700',
          className,
        )}
      >
        {measured && (
          <div
            className="h-full rounded-full bg-brand-500 transition-[width] duration-500"
            style={{ width: `${pct}%` }}
          />
        )}
      </div>
      {measured ? (
        <span className="font-mono text-xs tabular-nums text-slate-200">
          {formatNumber(value, 0)}
        </span>
      ) : (
        <span className="text-2xs text-slate-500">Not measured</span>
      )}
      {children}
    </div>
  )
}

/**
 * The rubric's score **with** its coverage, which is the only way either may be
 * shown.
 *
 * `score` is the weighted mean over the axes that were measured and `coverage` is
 * the share of the rubric's weight those axes carried. A score without its coverage
 * reads as a stronger measurement than it is — 90 over a fifth of the rubric is not
 * 90 — and that ambiguity is what `infra/sql/012_score_and_coverage.sql` was written
 * to remove. Enforcing the pair here rather than in each table means a new caller
 * cannot reintroduce it: pass both, or the cell says the row is not scored.
 */
export function ScoreMeter({
  score,
  coverage,
  className,
}: {
  score: number | null | undefined
  coverage: number | null | undefined
  className?: string
}) {
  const hasScore = score !== null && score !== undefined
  const hasCoverage = coverage !== null && coverage !== undefined

  // Not `hasScore && hasCoverage` folded into the Meter's own null path: a row with
  // a score and no coverage is a different fault from a row with neither, and
  // saying so is what stops it being read as "scored, coverage merely not shown".
  if (hasScore && !hasCoverage) {
    return (
      <span className="text-2xs text-amber-400" title="The API sent a score with no coverage. They are stored and served together, so this row predates infra/sql/012_score_and_coverage.sql or the response was truncated — the score is not shown, because it cannot be read without knowing how much of the rubric it covers.">
        Score without coverage
      </span>
    )
  }
  if (!hasScore) {
    return <span className="text-2xs text-slate-500">Not scored</span>
  }

  return (
    <Meter value={score} label="rubric score" className={className}>
      <span
        className="text-2xs tabular-nums text-slate-500"
        title="The share of the rubric's weight carried by the axes that were actually measured. The score is the weighted mean over those axes only."
      >
        {Math.round((coverage as number) * 100)}% measured
      </span>
    </Meter>
  )
}
